import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { PIPELINE_STAGES } from '../shared/constants';
import { getLocalUser } from '../utils/authHelpers';

export interface PipelineStageConfig {
  id: string;
  name: string;
  description: string;
  closing_probability: number;
  order: number;
}

const DEFAULT_STAGES: PipelineStageConfig[] = PIPELINE_STAGES.map((s, i) => ({
  id: s.id,
  name: s.name,
  description: s.description,
  closing_probability: s.id === 'closed_won' ? 100 : s.id === 'closed_lost' ? 0 : (i + 1) * 15,
  order: i + 1,
}));

export function usePipelineStagesConfig() {
  const [stages, setStages] = useState<PipelineStageConfig[]>(DEFAULT_STAGES);
  const [isLoading, setIsLoading] = useState(true);

  const fetchStages = useCallback(async () => {
    try {
      const localUser = getLocalUser();
      if (!localUser?.client_id) {
        setStages(DEFAULT_STAGES);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_settings')
        .select('pipeline_stages')
        .eq('client_id', localUser.client_id)
        .not('pipeline_stages', 'is', null)
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data?.pipeline_stages && Array.isArray(data.pipeline_stages) && data.pipeline_stages.length > 0) {
        const sorted = [...data.pipeline_stages].sort((a: PipelineStageConfig, b: PipelineStageConfig) => a.order - b.order);
        setStages(sorted);
      } else {
        setStages(DEFAULT_STAGES);
      }
    } catch {
      setStages(DEFAULT_STAGES);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveStages = useCallback(async (newStages: PipelineStageConfig[]): Promise<boolean> => {
    try {
      const localUser = getLocalUser();
      if (!localUser?.client_id || !localUser?.email) return false;

      const ordered = newStages.map((s, i) => ({ ...s, order: i + 1 }));

      const { error } = await supabase
        .from('user_settings')
        .update({ pipeline_stages: ordered } as any)
        .eq('client_id', localUser.client_id);

      if (error) throw error;

      setStages(ordered);
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    fetchStages();
  }, [fetchStages]);

  return { stages, isLoading, refetch: fetchStages, saveStages, DEFAULT_STAGES };
}
